// ⚠️  MOBILE-FIRST. Cards must be fully tap-target (entire card clickable, not just a button).
// START button stays ≥44px; whole card kept clickable so fat-finger taps don't miss.
// Squats is the canonical first exercise — always pinned to the top of the list.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listExercises, listWorkouts, personalBests } from "../lib/api";
import { Cpu, Dumbbell, Folder, Play, Trophy } from "lucide-react";

const CATEGORIES = ["all", "lower_body", "upper_body", "core", "cardio", "yoga", "full_body", "flexibility"];
const DIFFICULTIES = ["all", "beginner", "intermediate", "advanced"];

export default function ExerciseLibrary() {
    const [exercises, setExercises] = useState([]);
    const [recentWorkouts, setRecentWorkouts] = useState([]);
    const [bests, setBests] = useState({});
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("all");
    const [diff, setDiff] = useState("all");
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await listExercises();
                setExercises(data);
            } finally { setLoading(false); }
        })();
        // Users tend to repeat one go-to exercise daily — surface it at the top.
        listWorkouts(20)
            .then((r) => setRecentWorkouts(r.data || []))
            .catch((err) => console.warn("[library] recent workouts fetch failed:", err?.message || err));
        personalBests()
            .then((r) => setBests(r.data || {}))
            .catch((err) => console.warn("[library] personal bests fetch failed:", err?.message || err));
    }, []);

    // Best single-session score for an exercise, formatted per type.
    const bestFor = (ex) => {
        const b = bests[ex.exercise_id];
        if (!b) return null;
        if (ex.config?.type === "timed_hold") return b.hold_seconds > 0 ? `${Math.floor(b.hold_seconds)}s` : null;
        return b.reps > 0 ? `${b.reps}` : null;
    };

    const filtered = useMemo(() => {
        const matches = exercises.filter(e =>
            (cat === "all" || e.category === cat) &&
            (diff === "all" || e.difficulty === diff)
        );
        // Squats first (stable sort keeps the rest alphabetical).
        return matches.sort((a, b) => (a.slug === "squats" ? -1 : b.slug === "squats" ? 1 : 0));
    }, [exercises, cat, diff]);

    // Up to 3 most-repeated recently-trained exercises ("your usual").
    const usualExercises = useMemo(() => {
        const counts = new Map();
        for (const w of recentWorkouts) {
            if (w.status !== "completed" || !w.exercise_id) continue;
            counts.set(w.exercise_id, (counts.get(w.exercise_id) || 0) + 1);
        }
        return [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([id]) => exercises.find((e) => e.exercise_id === id))
            .filter(Boolean);
    }, [recentWorkouts, exercises]);

    return (
        <div data-testid="library-page">
            <div className="desktop-only">
                <div className="page-head">
                    <div>
                        <div className="eyebrow">Exercise library</div>
                        <h1 className="page">EXERCISES</h1>
                        <div className="page-sub">{"// "}<b>{exercises.length} exercises</b>{" - pick one and your camera does the counting"}</div>
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="btn btn-ghost">
                        <Dumbbell size={16} /> Dashboard
                    </button>
                </div>

                <div className="filters">
                    <div className="filter-line">
                        <span className="fl-label"><Folder size={13} /> Category</span>
                        {CATEGORIES.map(c => (
                            <button key={c} onClick={() => setCat(c)} data-testid={`filter-cat-${c}`}
                                className={`chip ${cat === c ? "on" : ""}`}>
                                {c.replace("_", " ").toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <div className="filter-line">
                        <span className="fl-label"><Cpu size={13} /> Difficulty</span>
                        {DIFFICULTIES.map(d => (
                            <button key={d} onClick={() => setDiff(d)} data-testid={`filter-diff-${d}`}
                                className={`chip ${diff === d ? "on cy" : ""}`}>
                                {difficultyLabel(d)}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? <div className="mt-8 label-tactical">Loading…</div> : (
                    <div className="reg-grid">
                        {filtered.map(ex => (
                            <ExerciseCard key={ex.exercise_id} ex={ex} best={bestFor(ex)} onStart={() => navigate(`/workout/${ex.exercise_id}`)} />
                        ))}
                        {filtered.length === 0 && <div className="text-zinc-500">No exercises match.</div>}
                    </div>
                )}
            </div>

            <div className="mobile-only">
                <div className="m-ph">
                    <div className="eyebrow">Pick a move</div>
                    <h1>Train</h1>
                    <div className="sub">{"// "}<b>{filtered.length} of {exercises.length}</b>{" exercises - tap to start"}</div>
                </div>

                {usualExercises.length > 0 && (
                    <>
                        <div className="m-sec" style={{ marginTop: 6 }}><span className="lab">Your usual</span></div>
                        {usualExercises.map(ex => (
                            <MobileExerciseRow key={`usual-${ex.exercise_id}`} ex={ex} best={bestFor(ex)} onStart={() => navigate(`/workout/${ex.exercise_id}`)} testid={`usual-${ex.slug}`} />
                        ))}
                        <div className="m-sec"><span className="lab">All exercises</span></div>
                    </>
                )}

                <div className="chips-row">
                    {CATEGORIES.map(c => (
                        <button key={c} onClick={() => setCat(c)} className={`chip ${cat === c ? "on" : ""}`}>
                            {c.replace("_", " ").toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="chips-row">
                    {DIFFICULTIES.map(d => (
                        <button key={d} onClick={() => setDiff(d)} className={`chip ${diff === d ? "on cy" : ""}`}>
                            {difficultyLabel(d)}
                        </button>
                    ))}
                </div>

                {loading ? <div className="label-tactical">Loading...</div> : filtered.map(ex => (
                    <MobileExerciseRow key={ex.exercise_id} ex={ex} best={bestFor(ex)} onStart={() => navigate(`/workout/${ex.exercise_id}`)} />
                ))}
                {!loading && filtered.length === 0 && <div className="text-sm text-[var(--tx-3)]">No exercises match.</div>}
            </div>
        </div>
    );
}

function ExerciseCard({ ex, best, onStart }) {
    const level = levelFor(ex.difficulty);
    return (
        <button
            type="button"
            onClick={onStart}
            className="pkg text-left w-full focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            data-testid={`exercise-card-${ex.slug}`}
        >
            <div className="pkg-vis">
                <span className="badge cat">{ex.category.replace("_", " ").toUpperCase()}</span>
                <span className={`badge cx ${level.badge}`}>{level.label}</span>
                <Dumbbell size={56} className="glyph" />
            </div>
            <div className="pkg-body">
                <div className="pkg-name">{ex.name.toUpperCase()}</div>
                <div className="pkg-meta">{(ex.muscle_groups || []).slice(0, 3).join(" - ") || ex.category.replace("_", " ")}</div>
                <div className="pkg-desc line-clamp-3">{ex.instructions}</div>
                <div className="pkg-foot">
                    <span className="pkg-target">
                        {ex.config?.type === "timed_hold" ? `Target ${ex.target_duration_sec || "?"}s` : `Target ${ex.target_reps} reps`}
                        {best && <span className="ml-2 text-[#FFD25A]" data-testid={`pb-${ex.slug}`}><Trophy size={11} className="inline -mt-0.5" /> PB {best}</span>}
                    </span>
                    <span className="btn btn-primary btn-sm pointer-events-none" data-testid={`start-exercise-${ex.slug}`}>
                        <Play size={14} /> Start
                    </span>
                </div>
            </div>
        </button>
    );
}

function difficultyLabel(value) {
    if (value === "beginner") return "EASY";
    if (value === "intermediate") return "MEDIUM";
    if (value === "advanced") return "HARD";
    return "ALL";
}

function levelFor(value) {
    if (value === "beginner") return { label: "EASY", badge: "green" };
    if (value === "intermediate") return { label: "MEDIUM", badge: "cyan" };
    return { label: "HARD", badge: "amber" };
}

function MobileExerciseRow({ ex, best, onStart, testid }) {
    const level = levelFor(ex.difficulty);
    return (
        <button type="button" className="move" onClick={onStart} data-testid={testid}>
            <div className="thumb"><Dumbbell /></div>
            <div className="mid">
                <div className="mn">{ex.name.toUpperCase()}</div>
                <div className="mmeta">
                    <span>{ex.category.replace("_", " ").toUpperCase()}</span>
                    <span className={`badge ${level.badge}`} style={{ padding: "2px 6px", fontSize: 9 }}>{level.label}</span>
                    <span>{ex.config?.type === "timed_hold" ? `${ex.target_duration_sec || "?"}s` : `${ex.target_reps} reps`}</span>
                    {best && <span style={{ color: "var(--gold)" }} data-testid={`m-pb-${ex.slug}`}>PB {best}</span>}
                </div>
            </div>
            <span className="play"><Play /></span>
        </button>
    );
}
